import { useEffect, useMemo } from 'react'
import { Flex, Box, Field, Heading, Input, Label, useModal, Loading } from 'ui'
import { track } from 'lib/amplitude'
import { Footer } from '../../Footer'
import { FormProvider, useForm } from 'react-hook-form'
import { PaymentForm, paymentSchema, PaymentTypeEnum } from '../../schemas'
import { useShiftEditor } from '../../useShiftEditor'
import { zodResolver } from '@hookform/resolvers/zod'
import { PaymentTypeSelector } from './PaymentTypeSelector'
import { FiDollarSign } from 'react-icons/fi'
import styled from 'styled-components'
import { BonusSelector } from './BonusSelector'
import { ChargeSummary } from './ChargeSummary'
import { PayGuidance } from './PayGuidance'
import { BonusGuidance } from './BonusGuidance'
import { TryoutLabel } from 'components/tryouts/TryoutLabel'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useEmployerGuidance } from 'queries/shift'
import { useCompanyPosition } from 'hooks/useCompanyPosition'
import { useShiftTime } from '../../useShiftTime'
import { BillRatePayNotice } from './BillRatePayNotice'
import { useTrackOnDemandDeliveryPageView } from '../../useTrackOnDemandDeliveryPageView'
import { BillRateChargeSummary } from './BillRateChargeSummary'
import { resolveEditorBillRate } from '../../resolveEditorBillRate'

const SHIFT_LEAD_UPCHARGE_PERCENTAGE = 20
const SHIFT_LEAD_PAY_TO_SHIFT_PAY_RATIO =
  SHIFT_LEAD_UPCHARGE_PERCENTAGE / 100 + 1

const PayBox = styled(Box)`
  position: relative;
  ${Label} {
    position: absolute;
    top: 0.7rem;
    left: 0;
  }
`

const HIGH_HOURLY_PAY_WARNING_THRESHOLD = 50

export function PaymentPage() {
  useEffect(() => {
    track('impression, ShiftEditor_Pay_page')
  }, [])
  useTrackOnDemandDeliveryPageView('Pay')
  const isInternalCompany = useGateValue(FeatureGates.InternalCompanyTreatment)
  const { shiftData, setShiftData, goNext, isTryout, isNew, editData } =
    useShiftEditor()
  const position = useCompanyPosition(shiftData.details?.positionId)
  const originalPositionId = editData?.position?.id
    ? Number(editData.position.id)
    : undefined
  // A shift's bill rate is locked in at creation, so an edit with an unchanged
  // position uses the shift's stored rate; creating or switching position uses
  // the position's current bill rate.
  const { billRate } = resolveEditorBillRate({
    isNew,
    originalPositionId,
    currentPositionId: shiftData.details?.positionId,
    shiftBillRate: shiftData.details?.billRate,
    positionBillRate: position?.billRate,
  })
  const isBillRate = billRate !== undefined && billRate !== null
  const allowZeroPay = isInternalCompany || isBillRate
  const methods = useForm<PaymentForm>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      ...(shiftData?.payment || {
        pay: 0,
        paymentType: PaymentTypeEnum.Hourly,
        bonus: 0,
        customBonus: 0,
      }),
      allowZeroPay,
    },
  })
  const {
    setValue,
    watch,
    register,
    formState: { errors },
    handleSubmit,
  } = methods

  const paymentType = watch('paymentType')
  const pay = watch('pay')
  const shiftLeadPay = watch('shiftLeadPay')
  const { showModal } = useModal()
  const shiftLeadRecommendedPay = useMemo(() => {
    if (!pay) return null

    return Math.ceil(pay * SHIFT_LEAD_PAY_TO_SHIFT_PAY_RATIO)
  }, [pay])

  useEffect(() => {
    setValue('allowZeroPay', allowZeroPay)
  }, [allowZeroPay])

  const { durationInMinutesMinusLunch } = useShiftTime({
    date: shiftData.schedule?.selectedDays?.[0],
    startTime: shiftData.schedule?.startTime?.value,
    endTime: shiftData.schedule?.endTime?.value,
    lunchLength: shiftData.schedule?.lunchLength?.value,
    timezone: shiftData.schedule?.timezone,
  })

  const shiftDurationHours = (durationInMinutesMinusLunch || 0) / 60

  const { data: employerGuidance, isLoading: isEmployerGuidanceLoading } =
    useEmployerGuidance({
      positionId: shiftData?.details?.positionId as number,
      locationId: shiftData?.details?.locationId as number,
      mileage: shiftData?.details?.expectedMileage,
      shiftDurationHours: shiftDurationHours,
    })

  const onSubmit = handleSubmit((data) => {
    // Clear stale bonus if bonus is hidden
    const payment = employerGuidance?.hideBonus
      ? { ...data, bonus: 0, customBonus: 0 }
      : data
    const submitAndGoNext = () => {
      setShiftData({
        payment,
      })
      goNext()
    }

    if (isBillRate) {
      submitAndGoNext()
      return
    }

    const totalBonuses = (data.customBonus || 0) + (data.bonus || 0)

    const isHighHourlyRate =
      data.paymentType === PaymentTypeEnum.Hourly &&
      data.pay > HIGH_HOURLY_PAY_WARNING_THRESHOLD

    const isHourlyPayBelowMinimum =
      employerGuidance?.minimumPayRate &&
      data.paymentType === PaymentTypeEnum.Hourly &&
      data.pay + totalBonuses / shiftDurationHours <
        employerGuidance?.minimumPayRate

    const isLumpSumBelowMinimum =
      employerGuidance?.minimumPayLumpSum &&
      data.paymentType === PaymentTypeEnum.LumpSum &&
      data.pay + totalBonuses < employerGuidance.minimumPayLumpSum

    const isBelowMinimumPay = isHourlyPayBelowMinimum || isLumpSumBelowMinimum

    if (isHighHourlyRate) {
      showModal({
        title: 'Confirm Hourly Rate',
        content: `The rate of $${data.pay}/hr seems unusually high, please confirm you have the correct rate.`,
        onOk: ({ closeModal }) => {
          closeModal()
          submitAndGoNext()
        },
      })
    } else if (isBelowMinimumPay) {
      showModal({
        title: 'Minimum Pay Rate',
        showCancel: false,
        content: `The total pay for this shift is below the minimum amount required by California regulations. Please increase the pay to meet the minimum requirement.`,
        onOk: ({ closeModal }) => {
          closeModal()
        },
      })
    } else {
      submitAndGoNext()
    }
  })

  if (isEmployerGuidanceLoading) {
    return <Loading />
  }

  return (
    <Box>
      <Heading level={[2, 1]} my={4}>
        Enter Pay Detail
      </Heading>
      {isTryout ? (
        <Box mb={3}>
          <TryoutLabel />
        </Box>
      ) : null}
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Flex flexDirection={'row'} flexWrap={'wrap'}>
            <Box width={[1, 1, 1, 1, 3 / 5]}>
              {isBillRate ? (
                <BillRatePayNotice />
              ) : (
                <>
                  <Heading level={3}>Select Payment Type</Heading>
                  <Field mt={4} error={errors.paymentType?.message?.toString()}>
                    <PaymentTypeSelector />
                  </Field>
                  <Heading level={3} my={4} mb={2}>
                    Base Pay
                  </Heading>
                  <Field
                    mt={2}
                    label={
                      paymentType === PaymentTypeEnum.Hourly
                        ? 'Enter the hourly rate per worker for this shift'
                        : 'Enter the amount (per each worker) for the entire shift'
                    }
                    error={errors.pay?.message?.toString()}
                  >
                    <PayBox>
                      <Input
                        aria-label={'Enter Worker Pay Amount'}
                        block={true}
                        max={100000}
                        step={'0.01'}
                        type={'number'}
                        {...register('pay')}
                        placeholder={'Enter an amount'}
                        icon={<FiDollarSign />}
                      />
                      {paymentType === PaymentTypeEnum.Hourly && pay ? (
                        <Label
                          style={{
                            transform: `translateX(calc(${
                              (pay ?? 0).toString().length
                            }ch + 5rem)`,
                          }}
                        >
                          Per Hour
                        </Label>
                      ) : null}
                    </PayBox>
                  </Field>
                  {shiftData.staff?.numShiftLeads ? (
                    <Field
                      mt={2}
                      label={
                        paymentType === PaymentTypeEnum.Hourly
                          ? 'Enter the hourly rate per shift lead for this shift'
                          : 'Enter the amount (per each shift lead) for the entire shift'
                      }
                      error={errors.shiftLeadPay?.message?.toString()}
                    >
                      <PayBox>
                        <Input
                          aria-label={'Enter Shift Lead Pay Amount'}
                          block={true}
                          max={100000}
                          step={'0.01'}
                          type={'number'}
                          {...register('shiftLeadPay')}
                          placeholder={
                            shiftLeadRecommendedPay
                              ? `e.g. ${shiftLeadRecommendedPay}`
                              : 'Enter an amount'
                          }
                          icon={<FiDollarSign />}
                        />
                        {paymentType === PaymentTypeEnum.Hourly &&
                        shiftLeadPay ? (
                          <Label
                            style={{
                              transform: `translateX(calc(${
                                (shiftLeadPay ?? 0).toString().length
                              }ch + 5rem)`,
                            }}
                          >
                            Per Hour
                          </Label>
                        ) : null}
                      </PayBox>
                    </Field>
                  ) : null}
                  {shiftData.details?.expectedMileage ? (
                    <PayGuidance
                      employerGuidance={employerGuidance}
                      isLoading={isEmployerGuidanceLoading}
                      shiftDurationHours={shiftDurationHours}
                    />
                  ) : null}
                </>
              )}
              {employerGuidance?.hideBonus ? null : (
                <>
                  <Heading level={3} my={4} mb={2}>
                    Bonus
                  </Heading>

                  <Field
                    mt={2}
                    label={'Select a one time bonus for this shift'}
                    error={errors.bonus?.message?.toString()}
                  >
                    <BonusSelector />
                  </Field>
                  <Field
                    mt={2}
                    label={'Or Enter a Custom Bonus Amount'}
                    error={errors.customBonus?.message?.toString()}
                  >
                    <Input
                      aria-label={'Enter Bonus Amount'}
                      type={'number'}
                      step={'0.01'}
                      {...register('customBonus')}
                      placeholder={'Enter an amount'}
                      icon={<FiDollarSign />}
                    />
                  </Field>
                  <BonusGuidance />
                </>
              )}
            </Box>
            <Box
              width={[1, 1, 1, 1, 2 / 5]}
              pl={[0, 0, 0, 0, 5]}
              mt={[4, 4, 4, 4, 0]}
            >
              {isBillRate && billRate ? (
                <BillRateChargeSummary billRate={billRate} />
              ) : (
                <ChargeSummary />
              )}
            </Box>
          </Flex>
          <Box width={[1, 1, 1, 1, 3 / 5]}>
            <Footer />
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}
